import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { JhPopupDrawer } from "./popupPackage";
import { JhText } from "./textPackage";
import { useIsMobile } from "../../../hooks/useDeviceType";
import { cn } from "../../../utils/tailwind-utils.js";
import {
  JhSelect,
  JhSelectContent,
  JhSelectItem,
  JhSelectTrigger,
  JhSelectValue
} from "./selectPackage";

export interface SelectOption {
  value?: string | number;
  id?: string | number;
  label: string;
  disabled?: boolean;
}

interface JhSelectWrapperProps {
  value?: string | number;
  placeholder?: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
  hasError?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  disabled?: boolean;
  emptyValue?: string | number; // Value that should show placeholder instead
  icon?: React.ReactNode;
  isSheetInMobile?: boolean;
  customTrigger?: (props: {
    onClick: () => void;
    displayText: string;
  }) => React.ReactNode;
}

const JhSelectWrapper: React.FC<JhSelectWrapperProps> = ({
  value,
  placeholder = "Select...",
  options,
  onValueChange,
  hasError = false,
  className,
  triggerClassName,
  contentClassName,
  itemClassName,
  disabled = false,
  emptyValue = -1,
  icon,
  isSheetInMobile = true,
  customTrigger
}) => {
  const isMobile = useIsMobile();
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);

  // Helper function to get option value (supports both 'value' and 'id')
  const getOptionValue = (option: SelectOption) => option?.value ?? option?.id;

  // Show placeholder if value equals emptyValue or is undefined/null
  const displayValue =
    value === emptyValue || value === undefined || value === null
      ? undefined
      : value?.toString();

  // Get display text for selected value
  const selectedOption = options.find(
    option => getOptionValue(option)?.toString() === displayValue
  );
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const handleMobileOptionSelect = (optionValue: string) => {
    onValueChange(optionValue);
    setIsMobileSheetOpen(false);
  };

  if (isMobile && isSheetInMobile) {
    // Mobile: Custom trigger + bottom sheet
    return (
      <>
        {customTrigger ? (
          customTrigger({
            onClick: () => !disabled && setIsMobileSheetOpen(true),
            displayText
          })
        ) : (
          <div
            onClick={() => !disabled && setIsMobileSheetOpen(true)}
            className={cn(
              "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 tw-root",
              hasError && "border-destructive focus:ring-destructive",
              disabled && "opacity-50 cursor-not-allowed",
              !disabled && "cursor-pointer",
              triggerClassName
            )}
          >
            <div className="flex items-center gap-2">
              {icon && <span className="flex-shrink-0">{icon}</span>}
              <span
                className={cn(
                  selectedOption ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {displayText}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        )}

        <JhPopupDrawer
          className="tw-root"
          open={isMobileSheetOpen}
          onClose={() => setIsMobileSheetOpen(false)}
          position="bottom"
        >
          {/* Header - matches Figma: 12px padding, gray text, semibold */}
          <div className="py-3">
            <JhText variant="body1" className="font-semibold text-slate-400">
              {placeholder}
            </JhText>
          </div>

          {/* Options - matches Figma: 12px/16px padding based on position */}
          <div className="flex flex-col">
            {options.map((option, index) => {
              const isSelected =
                getOptionValue(selectedOption) === getOptionValue(option);
              const isFirst = index === 0;
              const isLast = index === options.length - 1;

              return (
                <div
                  key={getOptionValue(option)}
                  onClick={() =>
                    !option.disabled &&
                    handleMobileOptionSelect(
                      getOptionValue(option)?.toString() || ""
                    )
                  }
                  className={cn(
                    "flex items-center justify-between cursor-pointer",
                    // Figma padding: 12px for first item, 16px for others
                    isFirst ? "py-3" : "py-4",
                    // Border: no top border for first item, bottom border except last
                    !isLast && "border-b border-slate-200",
                    option.disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <JhText
                    variant="body1"
                    className={cn(
                      "text-base font-normal",
                      isSelected ? "text-slate-900" : "text-slate-900"
                    )}
                  >
                    {option.label}
                  </JhText>

                  {isSelected && (
                    <Check
                      size={16}
                      strokeWidth={1.5}
                      className="text-secondary-foreground flex-shrink-0"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </JhPopupDrawer>
      </>
    );
  }

  // Desktop: Standard JhSelect
  return (
    <JhSelect
      value={displayValue}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <JhSelectTrigger
        className={cn(
          "w-full tw-root",
          hasError && "border-destructive focus:ring-destructive",
          triggerClassName
        )}
      >
        <div className="flex items-center gap-2">
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <JhSelectValue placeholder={placeholder} />
        </div>
      </JhSelectTrigger>

      <JhSelectContent className={cn("tw-root", contentClassName)}>
        {options.map(option => (
          <JhSelectItem
            key={getOptionValue(option)}
            value={getOptionValue(option)?.toString() || ""}
            disabled={option.disabled}
            className={cn("tw-root", itemClassName)}
          >
            {option.label}
          </JhSelectItem>
        ))}
      </JhSelectContent>
    </JhSelect>
  );
};

export default JhSelectWrapper;
